import React, { useState, useEffect } from 'react';
import { useToast } from './Toast';
import './ThresholdEditor.css';

interface Thresholds {
  zipBytesMax: number;
  filesMax: number;
  initialKbMax: number;
  subloadKbMax: number;
}

const DEFAULT_THRESHOLDS: Thresholds = {
  zipBytesMax: 200,
  filesMax: 40,
  initialKbMax: 200,
  subloadKbMax: 2500
};

export const ThresholdEditor: React.FC = () => {
  const { toast } = useToast();
  const [thresholds, setThresholds] = useState<Thresholds>(DEFAULT_THRESHOLDS);
  const [expanded, setExpanded] = useState(false);

  // Load thresholds from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('customThresholds');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Only keep fields that exist in current Thresholds interface
        setThresholds({
          zipBytesMax: parsed.zipBytesMax || DEFAULT_THRESHOLDS.zipBytesMax,
          filesMax: parsed.filesMax || DEFAULT_THRESHOLDS.filesMax,
          initialKbMax: parsed.initialKbMax || DEFAULT_THRESHOLDS.initialKbMax,
          subloadKbMax: parsed.subloadKbMax || DEFAULT_THRESHOLDS.subloadKbMax
        });
      } catch (error) {
        console.error('Failed to load custom thresholds:', error);
      }
    }
  }, []);

  const handleChange = (key: keyof Thresholds, value: number) => {
    setThresholds(prev => ({ ...prev, [key]: value }));
  };

  const saveThresholds = () => {
    localStorage.setItem('customThresholds', JSON.stringify(thresholds));
    toast('Custom thresholds saved! These will apply to your next upload.', 'success');
  };

  const resetDefaults = () => {
    setThresholds(DEFAULT_THRESHOLDS);
    localStorage.removeItem('customThresholds');
    toast('Reset to default thresholds', 'info');
  };

  if (!expanded) {
    return (
      <div className="threshold-editor-compact">
        <button onClick={() => setExpanded(true)} className="expand-btn">
          ⚙️ Customize Validation Thresholds
        </button>
      </div>
    );
  }

  return (
    <div className="threshold-editor">
      <div className="threshold-header">
        <h3>⚙️ Custom Validation Thresholds</h3>
        <button onClick={() => setExpanded(false)} className="close-btn">✕</button>
      </div>

      <div className="threshold-grid">
        <div className="threshold-item">
          <label>ZIP Size Max (KB)</label>
          <input
            type="number"
            value={thresholds.zipBytesMax}
            onChange={(e) => handleChange('zipBytesMax', parseInt(e.target.value) || 0)}
          />
        </div>

        <div className="threshold-item">
          <label>Max Files</label>
          <input
            type="number"
            value={thresholds.filesMax}
            onChange={(e) => handleChange('filesMax', parseInt(e.target.value) || 0)}
          />
        </div>

        <div className="threshold-item">
          <label>Initial Load Max (KB)</label>
          <input
            type="number"
            value={thresholds.initialKbMax}
            onChange={(e) => handleChange('initialKbMax', parseInt(e.target.value) || 0)}
          />
        </div>

        <div className="threshold-item">
          <label>Subload Max (KB)</label>
          <input
            type="number"
            value={thresholds.subloadKbMax}
            onChange={(e) => handleChange('subloadKbMax', parseInt(e.target.value) || 0)}
          />
        </div>
      </div>

      <div className="threshold-actions">
        <button onClick={saveThresholds} className="save-btn">Save Custom Rules</button>
        <button onClick={resetDefaults} className="reset-btn">Reset to Defaults</button>
      </div>

      <p className="threshold-note">
        Custom thresholds are saved and will apply to your next upload.
      </p>
    </div>
  );
};

// Helper function to get current thresholds (used by upload components)
export const getCustomThresholds = (): Thresholds | null => {
  const saved = localStorage.getItem('customThresholds');
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch {
      return null;
    }
  }
  return null;
};
